import {assert} from 'Asserts';
import {RegistrationStructuredTableAdapter} from 'notebook/integrations/registry/RegistrationStructuredTableAdapter';
import {
  RegistrationStructuredTableMetadata,
  RegistrationStructuredTableRowMetadata,
} from 'notebook/integrations/registry/RegistrationTableConstants';
import RegistrationTableSettingsModal from 'notebook/registration-tables/RegistrationTableSettingsModal';
import {performColumnOpAndSetLabelingStrategy} from 'notebook/registration-tables/RegistrationTableUtil';
import {StructuredTableFolderOption} from 'notebook/structured-tables/components/useSelectFolderOptionForTable';
import updateStructuredTableMetadata from 'notebook/structured-tables/util/updateStructuredTableMetadata';
import runModal from 'ui-library/runModal';

import {
  getRegistrationTableSettingsModalPropsForMonomerMapping,
  updateBasesColumnMonomerMappingConfig,
} from '../bases-columns/BasesColumnConstants';
import {ToolbarButtonActionFunction} from '../StructuredTableConstants';
import makeSimpleActionFunction from './makeSimpleActionFunction';

/**
 * An ActionFunction that lets the user view or update the table settings. Can only be changed if the editor
 * is unlocked and the table hasn't been submitted.
 */
export const updateRegistrationTableSettingsActionFunction: ToolbarButtonActionFunction<
  RegistrationStructuredTableMetadata,
  RegistrationStructuredTableRowMetadata
> = makeSimpleActionFunction({
  label: 'Settings',
  icon: 'settings',
  onInvoke: async ({structuredTableAdapter, structuredTable, locked, performTableOpOnTable}) => {
    const tagSchemaId = (structuredTableAdapter as RegistrationStructuredTableAdapter).getTagSchema(
      structuredTable
    )?.id;

    assert(tagSchemaId != null, 'Settings button clicked with no tag schema available');

    const registrationTableSettingsValues = await runModal(RegistrationTableSettingsModal, {
      labelingStrategy: structuredTable.metadata.labelingStrategy,
      insertType: structuredTable.metadata.insertType,
      folderConfig: {
        folderOption:
          structuredTable.metadata.folderOption ??
          StructuredTableFolderOption.DERIVE_FROM_PARENT_DOCUMENT_TYPE,
        folderId: structuredTable.metadata.folderId,
      },
      locked:
        locked ||
        /*
         * Only allow changing the labeling strategy if the editor is unlocked and we
         * don't have any registered rows.
         */
        structuredTable.rows.some((row) => row.isFinalized),
      tagSchemaId,
      ...getRegistrationTableSettingsModalPropsForMonomerMapping(structuredTable.columns),
    });
    if (registrationTableSettingsValues == null) {
      return;
    }
    const table = structuredTableAdapter.toTable(structuredTable);

    const {
      labelingStrategy: newLabelingStrategy,
      monomerMapping: newMonomerMapping,
      folderConfig: newFolderConfig,
    } = registrationTableSettingsValues;

    if (newLabelingStrategy != null) {
      performColumnOpAndSetLabelingStrategy({
        table,
        performTableOpOnTable,
        labelingStrategy: newLabelingStrategy,
        isStructuredTable: true,
      });
    }
    if (newMonomerMapping != null) {
      updateBasesColumnMonomerMappingConfig({
        performTableOpOnTable,
        monomerMapping: newMonomerMapping,
      });
    }
    if (newFolderConfig != null) {
      updateStructuredTableMetadata<RegistrationStructuredTableMetadata>({
        performTableOpOnTable,
        metadata: newFolderConfig,
      });
    }
  },
  getTooltip: () => 'Settings',
  getDisabled: ({structuredTableAdapter, structuredTable, isProtected}) => {
    const tagSchema = (structuredTableAdapter as RegistrationStructuredTableAdapter).getTagSchema(
      structuredTable
    );
    const hasNoTagSchema = tagSchema == null;
    return hasNoTagSchema || Boolean(isProtected);
  },
  getHidden: () => false,
});
